import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { BookmarkSimpleIcon } from "@phosphor-icons/react";

export default function BookmarkButton({
  streamId,
  initialBookmarked,
  initialCount,
  size = "default",
  onChange,
}) {
  const { user } = useAuth();
  const [bookmarked, setBookmarked] = useState(!!initialBookmarked);
  const [count, setCount] = useState(initialCount ?? 0);
  const [busy, setBusy] = useState(false);
  const [hydrated, setHydrated] = useState(initialBookmarked !== undefined);

  // Lazily resolve the current bookmark state when the parent didn't
  // pass `initialBookmarked`. Without this, the icon would render
  // unfilled even for shows the user already bookmarked, which is
  // misleading and lets people accidentally re-bookmark.
  useEffect(() => {
    if (hydrated || !streamId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/streams/${streamId}`);
        if (cancelled) return;
        setBookmarked(!!data.is_bookmarked);
        if (typeof data.bookmark_count === "number") setCount(data.bookmark_count);
      } catch {
        /* network — leave default */
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrated, streamId, user]);

  const toggle = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!user) {
      toast.error("Log in to bookmark this show.");
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const { data } = bookmarked
        ? await api.delete(`/streams/${streamId}/bookmark`)
        : await api.post(`/streams/${streamId}/bookmark`);
      setBookmarked(data.is_bookmarked);
      if (typeof data.bookmark_count === "number") setCount(data.bookmark_count);
      onChange?.(data);
      if (data.is_bookmarked) {
        toast.success("We'll ping you when this show starts.");
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sizeCls =
    size === "sm"
      ? "h-8 px-2.5 text-xs gap-1"
      : size === "lg"
      ? "h-10 px-4 text-sm gap-1.5"
      : "h-9 px-3 text-xs gap-1.5";

  return (
    <button
      onClick={toggle}
      disabled={busy}
      className={`inline-flex items-center font-semibold tracking-wide border transition-colors ${sizeCls} ${
        bookmarked
          ? "bg-amber-500 text-white border-amber-500 hover:bg-amber-600"
          : "bg-white text-zinc-900 border-zinc-300 hover:border-amber-400 hover:text-amber-600"
      }`}
      data-testid={bookmarked ? "unbookmark-stream-btn" : "bookmark-stream-btn"}
      title={bookmarked ? "Bookmarked — we'll ping you when this starts" : "Bookmark to get notified"}
      aria-pressed={bookmarked}
    >
      <BookmarkSimpleIcon size={size === "lg" ? 16 : 13} weight={bookmarked ? "fill" : "regular"} />
      {bookmarked ? "Bookmarked" : "Bookmark"}
      {count > 0 && <span className="tabular-nums opacity-80">· {count}</span>}
    </button>
  );
}
